import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../providers/UserProvider';
import { hasApiAccess, isSuperAdmin } from '../auth';
import { CopyButton } from '../components/CopyButton';
import { ModalOverlay } from '../components/ModalOverlay';
import {
  modelOrDynamicVariantIsSelected,
  OpenRouterModelVariantTags,
} from '../components/OpenRouterModelVariants';
import { listApiKeys, createApiKey, revokeApiKey } from '../repositories/apiKeyRepository';
import { apiJson } from '../api';
import {
  getPipelineSettings,
  updatePipelineSetting,
} from '../repositories/pipelineSettingsRepository';
import { STATUS_THEME_COLORS } from '../utils/statusColors';
import { availableModelsFrom, serializeAvailableModels, SETTING_KEYS } from '../utils/settingKeys';
import { formatDate } from '../utils/formatTime';
import type { ApiKey } from '../types';

function msToDate(ms: number | null): string {
  return formatDate(ms, { fallback: '—' });
}

function ApiKeysWidget() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKey, setNewKey] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listApiKeys()
      .then(setKeys)
      .catch(() => setError('Failed to load API keys.'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const key = await createApiKey(newKeyName.trim() || undefined);
      setKeys(prev => [key, ...prev]);
      setNewKey(key);
      setNewKeyName('');
    } catch {
      setError('Failed to create API key.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    setRevoking(keyId);
    try {
      await revokeApiKey(keyId);
      setKeys(prev => prev.map(k => k.key_id === keyId ? { ...k, status: 'REVOKED' } : k));
    } catch {
      setError('Failed to revoke key.');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <section className="admin-panel">
      <h2>API Keys</h2>
      <p style={{ marginBottom: '1rem', color: 'var(--text-secondary, #888)' }}>
        API keys authenticate requests to <code>/api/v1/</code> endpoints.
        Keys are shown once at creation — store them securely.
      </p>

      {error && (
        <div style={{ color: STATUS_THEME_COLORS.error, marginBottom: '1rem' }}>{error}</div>
      )}

      {newKey?.full_key && (
        <ModalOverlay onClose={() => setNewKey(null)} className="api-key-modal">
            <h3>New API key created</h3>
            <p style={{ color: 'var(--text-secondary, #888)', marginBottom: '0.75rem' }}>
              Copy this key now. <strong>It will not be shown again.</strong>
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
              <code style={{
                flex: 1, padding: '0.5rem', background: 'var(--bg-secondary, #1e1e1e)',
                color: 'var(--text-on-dark, #e2e8f0)',
                borderRadius: 4, wordBreak: 'break-all', fontSize: '0.85rem',
              }}>
                {newKey.full_key}
              </code>
              <CopyButton text={newKey.full_key} />
            </div>
            <button className="primary-button" onClick={() => setNewKey(null)}>Done</button>
        </ModalOverlay>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1.5rem' }}>
        <input
          type="text"
          placeholder="Key name (optional)"
          value={newKeyName}
          onChange={e => setNewKeyName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !creating && handleCreate()}
          style={{ maxWidth: 280 }}
        />
        <button className="primary-button" onClick={handleCreate} disabled={creating}>
          {creating ? 'Creating…' : 'Generate key'}
        </button>
      </div>

      {loading ? (
        <div className="empty-panel">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="empty-panel">No API keys yet.</div>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.key_id} style={{ opacity: k.status === 'REVOKED' ? 0.5 : 1 }}>
                <td>{k.name || <span style={{ color: 'var(--text-secondary, #888)' }}>—</span>}</td>
                <td><code>{k.key_prefix}…</code></td>
                <td>
                  <span style={{
                    color: k.status === 'ACTIVE' ? 'var(--accent, #3b82f6)' : 'var(--muted, #888)',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                  }}>
                    {k.status}
                  </span>
                </td>
                <td>{msToDate(k.created_at)}</td>
                <td>{msToDate(k.last_used_at)}</td>
                <td>
                  {k.status === 'ACTIVE' && (
                    <button
                      className="danger-button ghost-button"
                      onClick={() => handleRevoke(k.key_id)}
                      disabled={revoking === k.key_id}
                    >
                      {revoking === k.key_id ? 'Revoking…' : 'Revoke'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface OpenRouterModel {
  id: string;
  name: string;
}

function AvailableModelsWidget() {
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [allModels, setAllModels] = useState<OpenRouterModel[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      getPipelineSettings(),
      apiJson<OpenRouterModel[]>('/api/admin/openrouter/models'),
    ])
      .then(([settings, models]) => {
        const storedModels = availableModelsFrom(settings);
        if (storedModels.length) {
          setSelectedModels(storedModels);
        }
        setAllModels(models.sort((a, b) => a.id.localeCompare(b.id)));
      })
      .catch(() => setError('Failed to load models.'))
      .finally(() => setLoading(false));
  }, []);

  const saveModels = async (updated: string[]) => {
    setSaving(true);
    setError(null);
    try {
      await updatePipelineSetting(SETTING_KEYS.availableModels, serializeAvailableModels(updated));
      setSelectedModels(updated);
    } catch {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id: string) => {
    const next = selectedModels.includes(id)
      ? selectedModels.filter((m) => m !== id)
      : [...selectedModels, id];
    saveModels(next);
  };

  const lowerSearch = search.toLowerCase();
  const filtered = allModels.filter(
    (m) => m.id.toLowerCase().includes(lowerSearch) || m.name.toLowerCase().includes(lowerSearch),
  );

  // Show selected first, then the rest
  const selected = filtered.filter((m) => modelOrDynamicVariantIsSelected(m.id, selectedModels));
  const unselected = filtered.filter((m) => !modelOrDynamicVariantIsSelected(m.id, selectedModels));
  const sorted = [...selected, ...unselected];

  return (
    <section className="admin-panel">
      <h2>Available Models</h2>
      <p style={{ marginBottom: '0.75rem', color: 'var(--text-secondary, #888)' }}>
        Choose which OpenRouter models appear in prompt lab selectors.
        {selectedModels.length > 0 && (
          <span style={{ marginLeft: 8, color: 'var(--text-primary, #e2e8f0)' }}>
            {selectedModels.length} selected
          </span>
        )}
      </p>

      {error && (
        <div style={{ color: STATUS_THEME_COLORS.error, marginBottom: '0.75rem' }}>{error}</div>
      )}

      {loading ? (
        <div className="empty-panel">Loading models from OpenRouter...</div>
      ) : (
        <>
          <input
            type="text"
            placeholder="Search models..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: '0.75rem', maxWidth: 420 }}
          />

          <div
            style={{
              maxHeight: 360,
              overflowY: 'auto',
              border: '1px solid var(--line, #333)',
              borderRadius: 4,
              background: 'var(--bg-secondary, #1e1e1e)',
            }}
          >
            {sorted.length === 0 ? (
              <div style={{ padding: '1rem', color: 'var(--text-secondary, #888)', fontSize: '0.85rem' }}>
                No models match "{search}"
              </div>
            ) : (
              sorted.map((m) => {
                const isSelected = selectedModels.includes(m.id);
                return (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '6px 12px',
                      borderBottom: '1px solid var(--line, #222)',
                      background: modelOrDynamicVariantIsSelected(m.id, selectedModels)
                        ? 'rgba(59, 130, 246, 0.08)'
                        : 'transparent',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: saving ? 'wait' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(m.id)}
                        disabled={saving}
                        style={{ accentColor: 'var(--primary, #3b82f6)' }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <code style={{ fontSize: '0.82rem', color: 'var(--text-primary, #e2e8f0)' }}>{m.id}</code>
                        {m.name !== m.id && (
                          <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--text-secondary, #888)' }}>
                            {m.name}
                          </span>
                        )}
                      </span>
                    </label>
                    <OpenRouterModelVariantTags
                      modelId={m.id}
                      selectedModels={selectedModels}
                      disabled={saving}
                      onToggle={toggle}
                    />
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </section>
  );
}

export default function SettingsPage() {
  const { user, isAdmin } = useUser();
  const navigate = useNavigate();
  // Mode-gated: super-admin implies admin, so &&-ing the mode-aware isAdmin
  // keeps these widgets out of an admin-mode-off view (issue #889).
  const superAdmin = isSuperAdmin(user) && isAdmin;
  const apiAccess = hasApiAccess(user);

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          className="ghost-button"
          onClick={() => navigate(-1)}
          style={{ padding: '4px 8px', fontSize: '0.85rem' }}
        >
          &larr; Back
        </button>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 600, margin: 0 }}>Settings</h1>
      </div>

      {isAdmin && (
        <section className="admin-panel">
          <h2>Administration</h2>
          <Link to="/admin" className="settings-admin-link">
            Open admin dashboard
          </Link>
        </section>
      )}

      {apiAccess && <ApiKeysWidget />}
      {superAdmin && <AvailableModelsWidget />}
    </div>
  );
}
